/**
 * The engine's view of the Rust point-cloud parsers.
 *
 * `src/wasmPointcloud.ts` has fronted the same crate for a while, but only in
 * the extension host, so the standalone page and the webview ran the
 * TypeScript parsers instead and the two implementations drifted. This module
 * is the shared one: it loads the crate through the registration loader (which
 * already resolves the package in all four builds - extension host, webview,
 * standalone page and Node) and marshals results into the packed typed arrays
 * the format registry expects.
 *
 * Everything here runs on the calling thread. That is what the TypeScript
 * parsers did too, so it is not a regression, and the caveat from
 * `wasmLoader.browser.ts` applies: a webview Web Worker is not a separate
 * process, so moving the work off the UI thread means the extension host, not
 * a worker.
 */
import { loadRegistrationWasm } from '../registration/wasmLoader';
/* eslint-enable @typescript-eslint/naming-convention */
export function marshalWasmPointCloud(r) {
    const out = {
        sourcePointCount: r.source_count || undefined,
        sourcePointIndices: r.take_source_indices?.(),
        vertexCount: r.vertex_count,
        hasColors: r.has_colors,
        hasNormals: r.has_normals,
        hasIntensity: r.has_intensity,
        positionsArray: r.take_positions(),
        colorsArray: r.has_colors ? r.take_colors() : null,
        normalsArray: r.has_normals ? r.take_normals() : null,
        intensityArray: r.has_intensity ? r.take_intensity() : null,
        bbox: r.bbox(),
        metadataJson: r.metadata_json,
        scalarFields: Object.fromEntries((r.scalar_field_names ?? []).map((name, i) => [name, r.take_scalar_at(i)])),
    };
    // The result owns wasm memory; the takes above have already copied it out.
    r.free?.();
    return out;
}
let loaded = null;
function loadPointcloudWasm() {
    if (!loaded) {
        loaded = loadRegistrationWasm().then(wasm => {
            const candidate = wasm;
            return candidate && typeof candidate.parse_xyz === 'function' ? candidate : null;
        });
    }
    return loaded;
}
/**
 * Decide whether a coloured XYZ file writes colours as ints (`4`) or floats
 * (`0.0156`) by scanning the head of the file. XYZ carries no type information,
 * so this is one decision per file - writers are consistent - and only the
 * colour columns (index >= 3) are inspected, since positions always have
 * decimals. Mirrors `detectXyzColorMode` in `src/wasmPointcloud.ts`; the two
 * exist separately only because that one takes a Node `Buffer`.
 */
export function detectXyzColorMode(data, variant) {
    if (variant !== 'xyzrgb') {
        return 'auto';
    }
    const sample = data.subarray(0, Math.min(data.length, 64 * 1024));
    let text = '';
    for (let i = 0; i < sample.length; i++) {
        text += String.fromCharCode(sample[i]);
    }
    const lines = text.split('\n');
    // Drop the last (possibly truncated) line of the sample.
    const limit = sample.length < data.length ? lines.length - 1 : lines.length;
    for (let li = 0; li < limit; li++) {
        const t = lines[li].trim();
        if (!t || t.startsWith('#')) {
            continue;
        }
        const toks = t.split(/\s+/);
        if (toks.length < 6) {
            continue;
        }
        for (let i = 3; i < toks.length; i++) {
            const tok = toks[i];
            if (tok.includes('.') || tok.includes('e') || tok.includes('E')) {
                return 'unit';
            }
        }
    }
    return 'byte';
}
/** Parse plain XYZ, XYZN or XYZRGB. Throws if the crate is unavailable. */
export async function parseXyzWasm(data, variant) {
    const wasm = await loadPointcloudWasm();
    if (!wasm) {
        throw new Error('point-cloud wasm unavailable');
    }
    return marshalWasmPointCloud(wasm.parse_xyz(data, variant, detectXyzColorMode(data, variant)));
}
/** Parse a PTS point cloud. Throws if the crate is unavailable. */
export async function parsePtsWasm(data) {
    const wasm = await loadPointcloudWasm();
    if (!wasm) {
        throw new Error('point-cloud wasm unavailable');
    }
    return marshalWasmPointCloud(wasm.parse_pts(data));
}
/**
 * Parse a PLY - either encoding, points or mesh, ordinary or 3D Gaussian
 * splat. Throws if the crate is unavailable or the file is malformed; there is
 * no second parser to fall back to.
 */
export async function parsePlyWasm(data) {
    const wasm = await loadPointcloudWasm();
    if (!wasm) {
        throw new Error('point-cloud wasm unavailable');
    }
    return marshalPly(wasm.parse_ply(data));
}
/**
 * Parse a PLY straight from a fetch response, without the file ever existing as
 * a JavaScript buffer.
 *
 * `parsePlyWasm` takes a `Uint8Array`, which means wasm-bindgen copies the
 * whole file into wasm memory before parsing - on a 200 MB point cloud that
 * copy, plus the `ArrayBuffer` the fetch allocated to hold the bytes in the
 * first place, cost more than the parse itself. Here the response body is
 * streamed directly into a buffer inside wasm memory and parsed where it lies.
 *
 * Falls back to the copying path when the response has no length to size the
 * buffer with, or no readable stream.
 */
export async function parsePlyFromResponse(response) {
    const wasm = await loadPointcloudWasm();
    if (!wasm) {
        throw new Error('point-cloud wasm unavailable');
    }
    const declared = Number(response.headers.get('content-length'));
    if (!wasm.memory || !response.body || !Number.isFinite(declared) || declared <= 0) {
        return marshalPly(wasm.parse_ply(new Uint8Array(await response.arrayBuffer())));
    }
    const ptr = wasm.alloc(declared);
    try {
        const reader = response.body.getReader();
        let written = 0;
        for (;;) {
            const { done, value } = await reader.read();
            if (done) {
                break;
            }
            if (written + value.byteLength > declared) {
                throw new Error(`PLY body is longer than its content-length (${declared})`);
            }
            // The view is rebuilt per chunk: any wasm allocation can grow the memory
            // and detach every view over the old buffer.
            new Uint8Array(wasm.memory.buffer, ptr, declared).set(value, written);
            written += value.byteLength;
        }
        if (written !== declared) {
            throw new Error(`PLY body ended early: ${written} of ${declared} bytes`);
        }
        return marshalPly(wasm.parse_ply_at(ptr, declared));
    }
    finally {
        wasm.dealloc(ptr, declared);
    }
}
function marshalPly(raw) {
    const header = JSON.parse(raw.metadata_json);
    const scalarFields = {};
    const names = raw.scalar_field_names;
    const intensity = raw.has_intensity ? raw.take_intensity() : null;
    if (intensity) {
        scalarFields.intensity = intensity;
    }
    names.forEach((name, index) => {
        scalarFields[name] = raw.take_scalar_at(index);
    });
    // Faces come back flattened - a run of indices per face - because a PLY face
    // list is not fixed-width. The viewer wants one object per face.
    const faceIndices = raw.take_face_indices();
    const faceSizes = raw.take_face_sizes();
    const faces = new Array(faceSizes.length);
    let cursor = 0;
    for (let i = 0; i < faceSizes.length; i++) {
        const size = faceSizes[i];
        const indices = new Array(size);
        for (let j = 0; j < size; j++) {
            indices[j] = faceIndices[cursor + j];
        }
        faces[i] = { indices };
        cursor += size;
    }
    const result = {
        vertexCount: raw.vertex_count,
        faceCount: raw.face_count,
        positionsArray: raw.take_positions(),
        colorsArray: raw.has_colors ? raw.take_colors() : null,
        normalsArray: raw.has_normals ? raw.take_normals() : null,
        intensityArray: intensity,
        scalarFields,
        faces,
        hasColors: raw.has_colors,
        hasNormals: raw.has_normals,
        hasIntensity: raw.has_intensity,
        isGaussianSplat: raw.is_gaussian_splat,
        format: header.format,
        version: header.version,
        comments: header.comments,
    };
    raw.free?.();
    return result;
}
/**
 * Parse a PCD point cloud in any of its three encodings (ascii, binary,
 * binary_compressed). Throws if the crate is unavailable or the file is
 * malformed - there is no second parser to fall back to.
 */
export async function parsePcdWasm(data) {
    const wasm = await loadPointcloudWasm();
    if (!wasm) {
        throw new Error('point-cloud wasm unavailable');
    }
    const result = marshalWasmPointCloud(wasm.parse_pcd(data));
    return { ...result, header: JSON.parse(result.metadataJson) };
}
/**
 * The parsed-file payload both hosts hand on - the webview through
 * `convertToUnifiedFormat`, the extension host through `postMessage`. Shared so
 * the two cannot describe the same parse differently, which they could while
 * each built this object beside its own JS parser.
 */
export function toPointCloudPayload(result, format, comments = []) {
    return {
        sourcePointCount: result.sourcePointCount,
        sourcePointIndices: result.sourcePointIndices,
        vertexCount: result.vertexCount,
        positionsArray: result.positionsArray,
        colorsArray: result.colorsArray,
        normalsArray: result.normalsArray,
        intensityArray: result.intensityArray,
        hasColors: result.hasColors,
        hasNormals: result.hasNormals,
        hasIntensity: result.hasIntensity,
        scalarFields: {
            ...result.scalarFields,
            ...(result.intensityArray ? { intensity: result.intensityArray } : {}),
        },
        detectedFormat: describeLayout(result),
        comments,
        format,
        useTypedArrays: true,
    };
}
/**
 * The `pcdData` payload, which carries the header on top of the points: the
 * viewer applies VIEWPOINT as the file's initial transform, and the file list
 * shows the field names.
 */
export function toPcdPayload(result) {
    const { header } = result;
    return {
        ...toPointCloudPayload(result, header.format, header.comments),
        width: header.width,
        height: header.height,
        fields: header.fields,
        size: header.size,
        type: header.type,
        count: header.count,
        viewpoint: header.viewpoint,
    };
}
/** Human-readable column layout, for the status line and the PTS payload. */
export function describeLayout(result) {
    return ('x y z' +
        (result.hasIntensity ? ' intensity' : '') +
        (result.hasColors ? ' r g b' : '') +
        (result.hasNormals ? ' nx ny nz' : ''));
}
/**
 * What a `.npy` or `.npz` holds, without decoding any of it.
 *
 * Cheap enough to call during file-type detection: it reads headers, and for an
 * archive only its directory. Returns an empty list rather than throwing when
 * the file is not readable at all, because the callers use it to *decide*
 * whether they can handle the file.
 */
export async function inspectNpyWasm(data) {
    const wasm = await loadPointcloudWasm();
    if (!wasm) {
        throw new Error('point-cloud wasm unavailable');
    }
    try {
        return JSON.parse(wasm.npy_inspect(data));
    }
    catch {
        return [];
    }
}
/**
 * Decode one array. `name` selects a member of an `.npz`; it is ignored for a
 * plain `.npy`, and an empty name takes the archive's first array.
 */
export async function readNpyWasm(data, name = '') {
    const wasm = await loadPointcloudWasm();
    if (!wasm) {
        throw new Error('point-cloud wasm unavailable');
    }
    const raw = wasm.npy_read(data, name);
    const array = {
        name: raw.name,
        shape: Array.from(raw.shape),
        dtype: raw.dtype,
        values: raw.take_values(),
    };
    raw.free?.();
    return array;
}
/**
 * True when the array is a point list rather than an image: NumPy carries no
 * hint of its own, so the shape ending in 3 (`(N,3)`, `(H,W,3)`, and so on) is
 * the whole test - the same one the TypeScript parser used.
 */
export function isNpyPointCloudShape(shape) {
    return shape.length >= 1 && shape[shape.length - 1] === 3;
}
